import React from 'react';
import { aborted, blocked, failed } from 'flare-chat-core/contracts/chat/operation-result';
import { useCanvasDraftAutosave } from './draft-autosave.js';
import { buildArtifactNodeInsertDraft } from './artifact-node-insert.js';
import { buildFieldPackAppliedDraft } from './field-pack-apply.js';

const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

function resolveSyncWorkspaceDraft(action = {}) {
  const payload = everyTruthy(() => Object(action).payload, () => typeof action.payload === 'object', () => !Array.isArray(action.payload))
    ? action.payload
    : {};
  const analysisPayload = everyTruthy(() => payload.analysis_payload, () => typeof payload.analysis_payload === 'object')
    ? payload.analysis_payload
    : {};
  const artifactDocument = everyTruthy(() => payload.artifact_document, () => typeof payload.artifact_document === 'object')
    ? payload.artifact_document
    : {};
  const latestRevision = everyTruthy(() => artifactDocument.latest_revision, () => typeof artifactDocument.latest_revision === 'object')
    ? artifactDocument.latest_revision
    : {};
  return String(
    firstTruthy(() => payload.content, () => analysisPayload.markdown, () => analysisPayload.content, () => analysisPayload.text, () => latestRevision.content, () => artifactDocument.content, () => ''),
  ).trim();
}

function resolveActionTargetTab(action = {}, fallbackTabKey = '') {
  const payload = everyTruthy(() => Object(action).payload, () => typeof action.payload === 'object', () => !Array.isArray(action.payload))
    ? action.payload
    : {};
  return firstTruthy(() => String(
    payload.target_tab
    || payload.active_tab
    || action.target_tab
    || action.active_tab
    || fallbackTabKey
    || 'requirement',
  ).trim(), () => 'requirement');
}

export function resolveCanvasToolbarActionDraft(options) {
  const { action, actionKey, artifactDocument, canvasActiveTabKey, currentDraftBaselineContent, resolvedActiveIntakeContent } = { action: {}, actionKey: '', artifactDocument: {}, canvasActiveTabKey: '', currentDraftBaselineContent: '', resolvedActiveIntakeContent: '', ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (actionKey === 'refresh_artifact_upkeep') {
    return '';
  }
  if (actionKey === 'sync_to_workspace') {
    return resolveSyncWorkspaceDraft(action);
  }
  if (actionKey === 'artifact_node_insert_preview') {
    return buildArtifactNodeInsertDraft({
      action,
      currentContent: currentDraftBaselineContent || resolvedActiveIntakeContent,
    });
  }
  if (actionKey === 'apply_field_pack_suggestion') {
    return buildFieldPackAppliedDraft({
      action,
      activeTabKey: canvasActiveTabKey,
      artifactDocument,
      currentContent: currentDraftBaselineContent || resolvedActiveIntakeContent,
    });
  }
  return '';
}

function resolveSourceAction(action = {}) {
  const normalizedAction = action && typeof action === 'object' && !Array.isArray(action)
    ? action
    : {};
  return Object.keys(normalizedAction).length > 0 ? normalizedAction : null;
}

export function resolveRefreshStructureContent(options) {
  const { artifactDocument, currentDraftBaselineContent, resolvedActiveIntakeContent, resolvedCanvasAnalysisPayload, targetTabKey, canvasAnalysisTabKey } = { artifactDocument: {}, currentDraftBaselineContent: '', resolvedActiveIntakeContent: '', resolvedCanvasAnalysisPayload: null, targetTabKey: '', canvasAnalysisTabKey: 'analysis', ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (targetTabKey === canvasAnalysisTabKey) {
    return String(
      Object(resolvedCanvasAnalysisPayload).markdown
      || Object(resolvedCanvasAnalysisPayload).content
      || currentDraftBaselineContent
      || '',
    ).trim();
  }
  return String(
    currentDraftBaselineContent
    || resolvedActiveIntakeContent
    || Object(artifactDocument).content
    || '',
  ).trim();
}

export function resolveCanvasToolbarActionSaveMeta(actionKey = '', action = {}) {
  if (actionKey === 'sync_to_workspace') {
    return {
      changeOperation: 'sync_to_workspace',
      changeSummary: '同步最新 Artifact 到工作区',
      sourceKind: 'workspace_sync',
      ...(resolveSourceAction(action) ? { sourceAction: resolveSourceAction(action) } : {}),
    };
  }
  if (actionKey === 'refresh_artifact_upkeep') {
    return {
      changeOperation: 'refresh_artifact_upkeep',
      changeSummary: '刷新结构视图',
      sourceKind: 'artifact_upkeep_refresh',
      ...(resolveSourceAction(action) ? { sourceAction: resolveSourceAction(action) } : {}),
    };
  }
  if (actionKey === 'artifact_node_insert_preview') {
    return {
      changeOperation: 'artifact_node_insert_preview',
      changeSummary: '应用智能结构节点',
      sourceKind: 'artifact_node_insert_preview',
    };
  }
  return {};
}

export function useCanvasDraftActions(options) {
  const { artifactDocument, canvasActiveTabKey, canvasAnalysisTabKey, canvasDraftEditing, canvasDraftEditorValue, canvasManualControlsEnabled, currentDraftBaselineContent, onCanvasDraftSaved, resolvedActiveIntakeContent, resolvedCanvasAnalysisPayload, setCanvasDraftEditing, setCanvasDraftEditorValue } = { artifactDocument: {}, canvasActiveTabKey: '', canvasAnalysisTabKey: 'analysis', canvasDraftEditing: false, canvasDraftEditorValue: '', canvasManualControlsEnabled: false, currentDraftBaselineContent: '', resolvedActiveIntakeContent: '', resolvedCanvasAnalysisPayload: null, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const currentEditor = React.useRef({ tab: canvasActiveTabKey, value: canvasDraftEditorValue,
    artifactRef: JSON.stringify(artifactDocument.artifact_ref) });
  currentEditor.current = { tab: canvasActiveTabKey, value: canvasDraftEditorValue,
    artifactRef: JSON.stringify(artifactDocument.artifact_ref) };
  const commitCanvasDraftValue = React.useCallback(async (draftValue, commitOptions) => {
    const { closeEditor, saveMeta, syncEditorValue, targetTabKey } = { closeEditor: false, saveMeta: {}, syncEditorValue: true, targetTabKey: canvasActiveTabKey, ...Object.fromEntries(Object.entries(Object(commitOptions)).filter(([, value]) => value !== undefined)) };
    const nextValue = String(draftValue ?? '');
    const resolvedTargetTabKey = String(firstTruthy(() => targetTabKey, () => canvasActiveTabKey, () => '')).trim();
    if (resolvedTargetTabKey !== canvasActiveTabKey) return blocked('artifact_target_unavailable');
    const editorBeforeSave = currentEditor.current;
    if (typeof onCanvasDraftSaved !== 'function') return blocked('artifact_save_unavailable');
    if (syncEditorValue) setCanvasDraftEditorValue(nextValue);
    const result = await onCanvasDraftSaved(nextValue, {
      ...(everyTruthy(() => saveMeta, () => typeof saveMeta === 'object') ? saveMeta : {}),
      activeTabKey: resolvedTargetTabKey,
      artifactRef: artifactDocument.artifact_ref,
    });
    if (result?.status !== 'completed') return result;
    if (closeEditor && currentEditor.current.tab === resolvedTargetTabKey
      && currentEditor.current.artifactRef === editorBeforeSave.artifactRef
      && [nextValue, editorBeforeSave.value].includes(currentEditor.current.value)) setCanvasDraftEditing(false);
    return result;
  }, [
    canvasActiveTabKey,
    artifactDocument,
    onCanvasDraftSaved,
    setCanvasDraftEditing,
    setCanvasDraftEditorValue,
  ]);
  const markCanvasDraftSaved = useCanvasDraftAutosave({
    documentIdentity: JSON.stringify([artifactDocument.artifact_ref?.scope, artifactDocument.artifact_ref?.artifact_type, artifactDocument.artifact_ref?.artifact_id]),
    activeTabKey: canvasActiveTabKey,
    baselineContent: currentDraftBaselineContent,
    canvasDraftEditing,
    canvasDraftEditorValue,
    enabled: canvasManualControlsEnabled,
    // The native Host queue accepts fulfilled writes. Preserve Xiaocai's typed
    // failure/partial result at this adapter boundary instead of marking it saved.
    onAutosave: async (nextValue, options) => {
      const result = await commitCanvasDraftValue(nextValue, options);
      if (result?.status !== 'completed') throw Object.assign(new Error(result?.error?.message ?? result?.status), { operationResult: result });
      return result;
    },
  });
  const saveCanvasDraftValue = React.useCallback((value, options) => markCanvasDraftSaved.save(value, options)
    .catch(error => error.operationResult ?? (error.name === 'AbortError' ? aborted() : failed(error, 'editProjectArtifact'))), [markCanvasDraftSaved]);
  const handleCanvasToolbarAction = React.useCallback(async (action) => {
    const actionKey = String(firstTruthy(() => Object(action).action_key, () => Object(action).key, () => '')).trim();
    if (!['apply_field_pack_suggestion', 'artifact_node_insert_preview', 'sync_to_workspace', 'refresh_artifact_upkeep'].includes(actionKey)) {
      return;
    }
    const targetTabKey = ['sync_to_workspace', 'refresh_artifact_upkeep'].includes(actionKey)
      ? resolveActionTargetTab(action, canvasActiveTabKey)
      : canvasActiveTabKey;
    const targetBaselineContent = targetTabKey === canvasActiveTabKey ? currentDraftBaselineContent : '';
    const isRefreshStructureAction = actionKey === 'refresh_artifact_upkeep';
    const nextValue = isRefreshStructureAction ? resolveRefreshStructureContent({
      artifactDocument,
      currentDraftBaselineContent: targetBaselineContent,
      resolvedActiveIntakeContent,
      resolvedCanvasAnalysisPayload,
      targetTabKey,
      canvasAnalysisTabKey,
    }) : resolveCanvasToolbarActionDraft({
      action,
      actionKey,
      artifactDocument,
      canvasActiveTabKey,
      currentDraftBaselineContent,
      resolvedActiveIntakeContent,
    });
    if (firstTruthy(() => !nextValue, () => everyTruthy(() => !isRefreshStructureAction, () => nextValue === targetBaselineContent))) {
      return;
    }
    const result = await saveCanvasDraftValue(nextValue, {
      closeEditor: false,
      saveMeta: resolveCanvasToolbarActionSaveMeta(actionKey, action),
      syncEditorValue: targetTabKey === canvasActiveTabKey,
      targetTabKey,
    });
    return result;
  }, [
    artifactDocument,
    canvasActiveTabKey,
    canvasAnalysisTabKey,
    resolvedCanvasAnalysisPayload,
    saveCanvasDraftValue,
    currentDraftBaselineContent,
    resolvedActiveIntakeContent,
  ]);

  return {
    commitCanvasDraftValue: saveCanvasDraftValue,
    handleCanvasToolbarAction,
    markCanvasDraftSaved,
  };
}

export default useCanvasDraftActions;
